import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Pencil, Plus } from "lucide-react";
import { toast } from "sonner";

import { useApiHeaders } from "@/hooks/use-api-headers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ConfigSelect } from "@/components/config-select";
import {
  configOptions,
  COUNTRY_OPTIONS,
  CURRENCY_OPTIONS,
  TIMEZONE_OPTIONS,
  WEIGHT_UNIT_OPTIONS,
} from "@/lib/config-options";
import { enCatalog, translate, type TFunction } from "@/i18n/catalog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { BrandLogoField } from "@/components/brand-logo-field";
import { BrandColourField } from "@/components/brand-colour-field";
// The same constant the brand applier uses: a value equal to the platform default means
// nobody chose a colour, so neither the swatch nor the app shell treats it as a brand.
import { isHexColor, PLATFORM_DEFAULT_COLOR } from "@/lib/brand";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

interface Org {
  id: string;
  name: string;
  slug: string;
  timezone: string;
  currency: string;
  default_weight_unit: string;
  contact_email: string | null;
  contact_phone: string | null;
  address: string | null;
  country_code: string | null;
  default_locale: string | null;
  primary_color: string | null;
  logo_key: string | null;
  active_sites: number;
  active_users: number;
  created_at: string;
}

/** Hardcoded until the i18n ticket lands `frontend/src/i18n/config.ts`, which owns the
 * canonical locale set — at which point this imports it instead. Storing a language here
 * does not change what language anything renders in; that is the i18n ticket's job, and
 * this column is the second rung of its resolution chain. */
const LANGUAGE_OPTIONS = [
  { value: "en", label: "English" },
  { value: "de", label: "German" },
] as const;

const UNSET_LANGUAGE_OPTIONS = [{ value: "", label: "Not set" }, ...LANGUAGE_OPTIONS] as const;

const LANGUAGE_LABELS: Record<string, string> = {
  en: "English",
  de: "German",
};

/** The shape the API and the `organisations_primary_color_hex` constraint both enforce. */

const createOrgSchema = z.object({
  name: z.string().min(1, "Name is required"),
  admin_email: z.string().email("Invalid email"),
  timezone: z
    .string()
    .refine((tz) => (Intl.supportedValuesOf?.("timeZone") ?? []).includes(tz), {
      message: "Pick a timezone from the list",
    })
    .default("Europe/London"),
  currency: z.string().default("GBP"),
  default_weight_unit: z.string().default("kg"),
  contact_email: z.string().email("Invalid email").or(z.literal("")).optional(),
  country_code: z.string().optional(),
  // Both optional at create time, and both mirroring the server: the API has accepted them since
  // CP1, the form just never offered them.
  default_locale: z.enum(["en", "de"]).or(z.literal("")).optional(),
  primary_color: z
    .string()
    .refine(isHexColor, "Use a six-digit hex colour, e.g. #0F172A")
    .or(z.literal(""))
    .optional(),
});

type CreateOrgForm = z.infer<typeof createOrgSchema>;

/** Mirrors the server validators added in CP1, so the form refuses what the API would
 * refuse — the API stays the authority (a paste or a stale bundle has to be caught there). */
const editOrgSchema = z.object({
  name: z.string().min(1, "Name is required"),
  timezone: z.string().min(1),
  currency: z.string().min(1),
  default_weight_unit: z.string().min(1),
  default_locale: z.enum(["en", "de"]).or(z.literal("")),
  country_code: z.string(),
  contact_email: z.string().email("Invalid email").or(z.literal("")),
  contact_phone: z.string(),
  address: z.string(),
  primary_color: z
    .string()
    .refine(isHexColor, "Use a six-digit hex colour, e.g. #0F172A")
    .or(z.literal("")),
});

type EditOrgForm = z.infer<typeof editOrgSchema>;

/** `/admin/*` is English by pinned decision 4, so the shared config lists resolve against the
 * English catalogue rather than the viewer's locale — the same arrangement
 * `wasteTypeLabelEnglish` has on `/admin/monitoring` (POS2-1134). */
const englishT: TFunction = (key, params) => translate([enCatalog], "en", key, params);

/** The shared country list, resolved once at module scope — declared after `englishT` because
 * it reads it. "Not set" is worded and offered exactly as the language field's is: always at
 * create, and at edit only for an org that has no country, since the PATCH drops nulls and
 * cannot clear one. */
const COUNTRY_LABELS = configOptions(englishT, COUNTRY_OPTIONS);
const UNSET_COUNTRY_OPTIONS = [{ value: "", label: "Not set" }, ...COUNTRY_LABELS];

export default function AdminOrganisationsPage() {
  const headers = useApiHeaders();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editOrg, setEditOrg] = useState<Org | null>(null);

  const orgsQuery = useQuery<Org[]>({
    queryKey: ["admin", "organisations"],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/admin/organisations`, { headers });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`${res.status}: ${text || res.statusText}`);
      }
      const json = await res.json();
      return json.data;
    },
    enabled: !!headers.Authorization.replace("Bearer ", ""),
  });

  const createMutation = useMutation({
    mutationFn: async (data: CreateOrgForm) => {
      const res = await fetch(`${API_URL}/api/v1/admin/organisations`, {
        method: "POST",
        headers,
        body: JSON.stringify(createBody(data)),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error?.message ?? "Failed to create organisation");
      }
      return res.json();
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "organisations"] });
      const emailSent = result?.data?.email_sent;
      if (emailSent === true) {
        toast.success("Organisation created. Invitation sent.");
      } else if (emailSent === false) {
        toast.warning("Organisation created, but invitation email failed to send.");
      } else {
        toast.success("Organisation created");
      }
      setOpen(false);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const editMutation = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: Record<string, string> }) => {
      const res = await fetch(`${API_URL}/api/v1/admin/organisations/${id}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error?.message ?? err.detail ?? "Failed to update organisation");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "organisations"] });
      // The brand read too: a platform admin editing their own org's colour should see the
      // shell repaint, not wait out the 30-minute staleTime.
      queryClient.invalidateQueries({ queryKey: ["organisation", "brand"] });
      toast.success("Organisation updated");
      setEditOrg(null);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm<CreateOrgForm>({
    resolver: zodResolver(createOrgSchema),
    defaultValues: { timezone: "Europe/London", currency: "GBP", default_weight_unit: "kg" },
  });

  const createColour = watch("primary_color");
  const onSubmit = (data: CreateOrgForm) => createMutation.mutate(data);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-medium">Organisations</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage all organisations on the platform.
          </p>
        </div>
        <Dialog
          open={open}
          onOpenChange={(v) => {
            setOpen(v);
            if (!v) reset();
          }}
        >
          <DialogTrigger asChild>
            <Button size="sm">
              <Plus className="mr-1 size-4" />
              New Organisation
            </Button>
          </DialogTrigger>
          <DialogContent className="flex max-h-[85vh] flex-col overflow-hidden">
            <DialogHeader>
              <DialogTitle>Create Organisation</DialogTitle>
            </DialogHeader>
            {/* The sites/kitchens dialog shape: the panel is capped, the fields scroll inside it, and
              the submit button stays pinned below them. Without it a tall form runs off the
              bottom of a laptop viewport taking its own Save button with it. */}
            <form onSubmit={handleSubmit(onSubmit)} className="flex min-h-0 flex-1 flex-col gap-4">
              <div className="-mx-6 min-h-0 flex-1 space-y-4 overflow-y-auto px-6">
                <div>
                  <label className="text-sm font-medium" htmlFor="name">
                    Name
                  </label>
                  <Input id="name" {...register("name")} />
                  {errors.name && (
                    <p className="mt-1 text-xs text-destructive">{errors.name.message}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium" htmlFor="admin_email">
                    Org Admin Email
                  </label>
                  <Input id="admin_email" type="email" {...register("admin_email")} />
                  {errors.admin_email && (
                    <p className="mt-1 text-xs text-destructive">{errors.admin_email.message}</p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium" htmlFor="country_code">
                      Country
                    </label>
                    {/* A picker, not free text: the country tier compares this against
                        `inventory_lists.country_code` exactly, and the catalogue admin now
                        offers the same list, so the two cannot drift by hand (POS2-1144). */}
                    <ConfigSelect
                      id="country_code"
                      options={UNSET_COUNTRY_OPTIONS}
                      {...register("country_code")}
                    />
                    <p className="mt-1 text-xs text-muted-foreground">
                      Drives the catalogue country tier.
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium" htmlFor="create-org-locale">
                      Language
                    </label>
                    {/* "Not set" is the starting state here, unlike the edit dialog: a new
                        organisation has no language until someone picks one. */}
                    <ConfigSelect
                      id="create-org-locale"
                      options={UNSET_LANGUAGE_OPTIONS}
                      {...register("default_locale")}
                    />
                    <p className="mt-1 text-xs text-muted-foreground">
                      The default for members who have not chosen their own language.
                    </p>
                  </div>
                </div>
                {/* Timezone gets its own row in both dialogs: its option labels are the longest
                    in the form ("America/Los_Angeles (PST)"), and at a third of a 512px panel
                    they truncated mid-word — as did "GBP — British Pound" beside them. */}
                <div>
                  <label className="text-sm font-medium" htmlFor="timezone">
                    Timezone
                  </label>
                  <ConfigSelect
                    id="timezone"
                    options={TIMEZONE_OPTIONS}
                    {...register("timezone")}
                  />
                  {errors.timezone && (
                    <p className="mt-1 text-xs text-destructive">{errors.timezone.message}</p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium" htmlFor="currency">
                      Currency
                    </label>
                    <ConfigSelect
                      id="currency"
                      options={configOptions(englishT, CURRENCY_OPTIONS)}
                      {...register("currency")}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium" htmlFor="default_weight_unit">
                      Weight Unit
                    </label>
                    <ConfigSelect
                      id="default_weight_unit"
                      options={configOptions(englishT, WEIGHT_UNIT_OPTIONS)}
                      {...register("default_weight_unit")}
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium" htmlFor="contact_email">
                    Contact Email
                  </label>
                  <Input id="contact_email" type="email" {...register("contact_email")} />
                  {errors.contact_email && (
                    <p className="mt-1 text-xs text-destructive">{errors.contact_email.message}</p>
                  )}
                </div>
                {/* `logoKey` is null by construction: an organisation that does not exist yet
                    has nothing to upload a logo to, so there is nothing to sample. */}
                <BrandColourField
                  t={englishT}
                  id="create-org-colour"
                  logoKey={null}
                  value={createColour ?? ""}
                  onChange={(hex) => setValue("primary_color", hex, { shouldValidate: true })}
                  error={errors.primary_color?.message}
                />
              </div>
              <Button
                type="submit"
                className="w-full"
                disabled={!isValid || createMutation.isPending}
              >
                {createMutation.isPending ? "Creating..." : "Create Organisation"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {orgsQuery.isLoading && (
        <div className="space-y-2">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      )}

      {orgsQuery.isError && (
        <div className="rounded border border-destructive/50 bg-destructive/10 p-3">
          <p className="text-sm font-medium text-destructive">Failed to load organisations</p>
          <p className="mt-1 font-mono text-xs text-destructive/80">
            {orgsQuery.error instanceof Error ? orgsQuery.error.message : "Unknown error"}
          </p>
        </div>
      )}

      {orgsQuery.data && orgsQuery.data.length === 0 && (
        <div className="flex flex-col items-center gap-2 py-12 text-center text-muted-foreground">
          <p className="text-sm">No organisations yet</p>
          <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
            Create your first organisation
          </Button>
        </div>
      )}

      {orgsQuery.data && orgsQuery.data.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>Country</TableHead>
              <TableHead>Language</TableHead>
              <TableHead className="text-right">Sites</TableHead>
              <TableHead className="text-right">Users</TableHead>
              <TableHead>Currency</TableHead>
              <TableHead>Timezone</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead className="text-right">Created</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {orgsQuery.data.map((org) => (
              <TableRow key={org.id}>
                <TableCell className="font-medium">
                  <span className="flex items-center gap-2">
                    <BrandSwatch color={org.primary_color} />
                    {org.name}
                  </span>
                </TableCell>
                <TableCell>
                  <Badge variant="secondary">{org.slug}</Badge>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {org.country_code ?? "—"}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {org.default_locale
                    ? (LANGUAGE_LABELS[org.default_locale] ?? org.default_locale)
                    : "—"}
                </TableCell>
                <TableCell className={countClass(org.active_sites)}>{org.active_sites}</TableCell>
                <TableCell className={countClass(org.active_users)}>{org.active_users}</TableCell>
                <TableCell>{org.currency}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{org.timezone}</TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {org.contact_email ?? "—"}
                </TableCell>
                <TableCell className="text-right text-xs text-muted-foreground">
                  {new Date(org.created_at).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <button
                    type="button"
                    aria-label={`Edit ${org.name}`}
                    className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
                    onClick={() => setEditOrg(org)}
                  >
                    <Pencil className="size-3.5" />
                  </button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {editOrg && (
        <EditOrgDialog
          org={editOrg}
          saving={editMutation.isPending}
          onClose={() => setEditOrg(null)}
          onSave={(body) => editMutation.mutate({ id: editOrg.id, body })}
        />
      )}
    </div>
  );
}

function countClass(value: number): string {
  return value > 0
    ? "text-right tabular-nums font-medium"
    : "text-right tabular-nums text-muted-foreground";
}

function BrandSwatch({ color }: { color: string | null }) {
  if (!color || color.toLowerCase() === PLATFORM_DEFAULT_COLOR) return null;
  return (
    <span
      aria-label={`Brand colour ${color}`}
      title={color}
      className="size-3 shrink-0 rounded-sm border border-border"
      style={{ backgroundColor: color }}
    />
  );
}

function EditOrgDialog({
  org,
  saving,
  onClose,
  onSave,
}: {
  org: Org;
  saving: boolean;
  onClose: () => void;
  onSave: (body: Record<string, string>) => void;
}) {
  const queryClient = useQueryClient();
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<EditOrgForm>({
    resolver: zodResolver(editOrgSchema),
    defaultValues: {
      name: org.name,
      timezone: org.timezone,
      currency: org.currency,
      default_weight_unit: org.default_weight_unit,
      default_locale: org.default_locale === "de" ? "de" : org.default_locale === "en" ? "en" : "",
      country_code: org.country_code ?? "",
      contact_email: org.contact_email ?? "",
      contact_phone: org.contact_phone ?? "",
      address: org.address ?? "",
      primary_color: org.primary_color ?? "",
    },
  });
  const colour = watch("primary_color");

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="flex max-h-[85vh] flex-col overflow-hidden sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit {org.name}</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={handleSubmit((data) => onSave(patchBody(data)))}
          className="flex min-h-0 flex-1 flex-col gap-4"
        >
          <div className="-mx-6 min-h-0 flex-1 space-y-4 overflow-y-auto px-6">
            <div>
              <label className="text-sm font-medium" htmlFor="edit-org-name">
                Name
              </label>
              <Input id="edit-org-name" {...register("name")} />
              {errors.name && (
                <p className="mt-1 text-xs text-destructive">{errors.name.message}</p>
              )}
            </div>

            <div>
              <p className="text-sm font-medium">Slug</p>
              <p className="mt-1 font-mono text-sm text-muted-foreground">{org.slug}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Fixed — it identifies the organisation everywhere it has already been used.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium" htmlFor="edit-org-country">
                  Country
                </label>
                {/* "Not set" only for an org that has none, the same rule the language field
                  beside it follows and for the same reason: the PATCH drops nulls, so choosing
                  it on an org that has a country would do nothing at all. */}
                <ConfigSelect
                  id="edit-org-country"
                  options={org.country_code ? COUNTRY_LABELS : UNSET_COUNTRY_OPTIONS}
                  current={org.country_code}
                  {...register("country_code")}
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Drives the catalogue country tier.
                </p>
              </div>
              <div>
                <label className="text-sm font-medium" htmlFor="edit-org-locale">
                  Language
                </label>
                {/* "Not set" is offered only to an org that has no default, so choosing it is
                  always a no-op. An org that has one cannot be handed back to the browser
                  here, because the PATCH drops nulls — see `patchBody`. */}
                <ConfigSelect
                  id="edit-org-locale"
                  options={org.default_locale ? LANGUAGE_OPTIONS : UNSET_LANGUAGE_OPTIONS}
                  current={org.default_locale}
                  {...register("default_locale")}
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  The default for members who have not chosen their own language.
                </p>
              </div>
            </div>

            {/* Timezone alone, currency and weight unit paired — the same rhythm the create
                dialog uses. Three across a 512px panel truncated all three mid-word. */}
            <div>
              <label className="text-sm font-medium" htmlFor="edit-org-timezone">
                Timezone
              </label>
              <ConfigSelect
                id="edit-org-timezone"
                options={TIMEZONE_OPTIONS}
                current={org.timezone}
                {...register("timezone")}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium" htmlFor="edit-org-currency">
                  Currency
                </label>
                <ConfigSelect
                  id="edit-org-currency"
                  options={configOptions(englishT, CURRENCY_OPTIONS)}
                  current={org.currency}
                  {...register("currency")}
                />
              </div>
              <div>
                <label className="text-sm font-medium" htmlFor="edit-org-unit">
                  Weight Unit
                </label>
                <ConfigSelect
                  id="edit-org-unit"
                  options={configOptions(englishT, WEIGHT_UNIT_OPTIONS)}
                  current={org.default_weight_unit}
                  {...register("default_weight_unit")}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium" htmlFor="edit-org-email">
                  Contact Email
                </label>
                <Input id="edit-org-email" type="email" {...register("contact_email")} />
                {errors.contact_email && (
                  <p className="mt-1 text-xs text-destructive">{errors.contact_email.message}</p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium" htmlFor="edit-org-phone">
                  Contact Phone
                </label>
                <Input id="edit-org-phone" {...register("contact_phone")} />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium" htmlFor="edit-org-address">
                Address
              </label>
              <Input id="edit-org-address" {...register("address")} />
            </div>

            <div>
              <BrandColourField
                t={englishT}
                id="edit-org-colour"
                logoKey={org.logo_key}
                value={colour}
                onChange={(hex) => setValue("primary_color", hex, { shouldValidate: true })}
                error={errors.primary_color?.message}
              />
            </div>

            {/* Shared with `/settings/organisation` (POS2-1146). `englishT` keeps this dialog
                English while the same component reads the customer's language over there. */}
            <BrandLogoField
              t={englishT}
              logoPath={`/admin/organisations/${org.id}/logo`}
              initialLogoKey={org.logo_key}
              onChanged={() => {
                queryClient.invalidateQueries({ queryKey: ["admin", "organisations"] });
                queryClient.invalidateQueries({ queryKey: ["organisation", "brand"] });
              }}
            />
          </div>

          <Button type="submit" className="w-full" disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** `OrgCreate.primary_color` is **not** nullable — it carries the platform default. So an empty
 * colour field means "leave it at the default", and the key is omitted rather than sent as a null,
 * which the API would refuse with a 422. The nullable fields beside it do take null.
 */
function createBody(form: CreateOrgForm): Record<string, unknown> {
  const { primary_color, ...rest } = form;
  return {
    ...rest,
    contact_email: rest.contact_email || null,
    country_code: rest.country_code || null,
    default_locale: rest.default_locale || null,
    ...(primary_color ? { primary_color } : {}),
  };
}

/** `PATCH /admin/organisations/{id}` drops nulls (`exclude_none`), so an empty field cannot
 * clear a stored value. Empty fields are therefore omitted rather than sent as a null that
 * would silently do nothing — the form never claims to clear something it cannot.
 *
 * No country-code special case any more: it used to be upper-cased here because the catalogue
 * country tier compares it exactly, and POS2-1144 moved that to `OrgUpdate` where every writer
 * gets it — this form, `/settings/organisation`, and anything calling the API directly. */
function patchBody(form: EditOrgForm): Record<string, string> {
  const body: Record<string, string> = {};
  for (const [field, value] of Object.entries(form)) {
    const trimmed = value.trim();
    if (trimmed) body[field] = trimmed;
  }
  return body;
}
